import {
  type AskPunttProjectFile,
  type AskPunttProjectScratchpadEntry,
} from "@mg/schemas/src/christo/askPunttProject";
import {
  CaretDown,
  CircleNotch,
  FileText,
  ImageSquare,
  PencilSimple,
  VideoCamera,
} from "@phosphor-icons/react";
import cx from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CitedClaimsPanel } from "./CitedClaimsPanel";
import { FileTagEditor } from "./OrgLabelEditor";
import { UsedConnectorsChips } from "./UsedConnectorsChips";

import {
  interactiveSurfaceClasses,
  interactiveSurfaceIconClasses,
} from "../../../components/InteractiveSurface";
import { downloadAsset } from "../../../utils/http";
import { type TicketMessageCitation } from "../utils/commentSourceUtils";
import {
  displayFileNameFromSource,
  type ProjectReferenceDocumentRow,
} from "../utils/projectContextDocuments";
import { type UsedConnector } from "../utils/usedConnectors";

/**
 * Right-hand "PROJECT CONTEXT" panel for the project workspace. Renders
 * user-uploaded, versioned project files. Owners get a pencil affordance to
 * edit the project's description / metadata.
 *
 * AGENT CONTRACT (#product 2026-08-04 / Dave / PR #4377 regression):
 * Reference Documents must stay on DownloadableReferenceRow (type label +
 * interactiveSurfaceClasses). Do NOT swap project docs to the chat
 * FileAttachmentButton gray chip — it blends into light project rails and
 * drops PDF/IMAGE/XLSX affordances. Keep CampaignDocsPanel.test.tsx red if
 * that regresses; fix the product, do not soften the assertions.
 */
function fileTypeIcon(type: string) {
  if (type === "image") return ImageSquare;
  if (type === "video") return VideoCamera;
  return FileText;
}

function fileTypeLabel(
  type: string,
  name: string,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  const ext = name.split(".").pop()?.toUpperCase();
  if (ext && ext.length <= 5 && ext !== name.toUpperCase()) return ext;
  if (type === "image") return t("campaignDocsPanel.fileTypeImage");
  if (type === "video") return t("campaignDocsPanel.fileTypeVideo");
  return t("campaignDocsPanel.fileTypePdf");
}

export function CampaignDocsPanel({
  documents,
  description,
  upcomingWork,
  details = [],
  usedConnectors = [],
  citedClaims = [],
  selectedCitationRuleId,
  onCitedClaimSelect,
  citedClaimsTestIdPrefix = "project-context-cited-claims",
  onEdit,
  panelOpen: panelOpenProp,
  onPanelOpenChange,
  projectId,
}: {
  /** One row per file version (newest first). */
  documents: ProjectReferenceDocumentRow[];
  /** The project's overall description, surfaced above the documents. */
  description?: string;
  /** Agent-maintained summary of next steps for this project. */
  upcomingWork?: string;
  /**
   * Surfaced agent scratch-pad entries (the agent flagged these for display).
   * Read-only — the agent maintains them; users cannot edit them here.
   */
  details?: AskPunttProjectScratchpadEntry[];
  /** External connectors invoked in the project chat. */
  usedConnectors?: readonly UsedConnector[];
  /** Knowledge rule citations from the focused assistant message. */
  citedClaims?: readonly TicketMessageCitation[];
  selectedCitationRuleId?: string | null;
  onCitedClaimSelect?: (ruleId: string) => void;
  citedClaimsTestIdPrefix?: string;
  /** Owner-only edit affordance for the project description / metadata. */
  onEdit?: () => void;
  /** Controlled expand state for the project context section header. */
  panelOpen?: boolean;
  onPanelOpenChange?: (open: boolean) => void;
  /** When set, each file row shows an editable tags control. */
  projectId?: string;
}) {
  const { t } = useTranslation("ui");
  const [panelOpenInternal, setPanelOpenInternal] = useState(true);
  const panelOpen = panelOpenProp ?? panelOpenInternal;
  const setPanelOpen = (next: boolean) => {
    onPanelOpenChange?.(next);
    if (panelOpenProp === undefined) setPanelOpenInternal(next);
  };
  const [docsOpen, setDocsOpen] = useState(true);
  const upcomingWorkText = upcomingWork?.trim() ?? "";

  return (
    <section
      className={`flex min-h-0 flex-col overflow-hidden rounded-xl border border-puntt-neutral-gray-5 bg-base-white ${panelOpen ? "h-full flex-1" : "shrink-0"}`}
      aria-label={t("campaignDocsPanel.projectContextLabel")}
      data-testid="project-context-panel"
      data-panel-expanded={panelOpen ? "true" : "false"}
    >
      <div className="flex shrink-0 items-center gap-1 border-b border-puntt-neutral-gray-5 px-4 py-3">
        <button
          type="button"
          onClick={() => setPanelOpen(!panelOpen)}
          aria-expanded={panelOpen}
          aria-label={t("campaignDocsPanel.toggleProjectContext")}
          data-testid="project-context-toggle"
          className="flex min-w-0 flex-1 items-center gap-2 bg-base-white text-left"
        >
          <h2 className="flex-1 text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-12">
            {t("campaignDocsPanel.projectContextLabel")}
          </h2>
          <CaretDown
            size={14}
            aria-hidden="true"
            className={`shrink-0 text-puntt-neutral-gray-11 transition-transform ${panelOpen ? "" : "-rotate-90"}`}
          />
        </button>
      </div>

      {panelOpen && (
        <div
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain p-4 [scrollbar-width:thin]"
          data-testid="project-context-scroll"
        >
          {(description || onEdit) && (
            <div data-testid="campaign-project-description">
              <div
                className="flex items-center gap-2"
                data-testid="campaign-project-description-header"
              >
                <h3 className="flex-1 text-[13px] font-bold text-puntt-neutral-gray-12">
                  {t("campaignDocsPanel.instructions")}
                </h3>
                {onEdit && (
                  <button
                    type="button"
                    onClick={onEdit}
                    aria-label={t("campaignDocsPanel.editProjectContext")}
                    data-testid="project-context-edit-button"
                    className="flex size-7 shrink-0 items-center justify-center rounded-md bg-transparent text-puntt-neutral-gray-11 transition-colors hover:text-puntt-neutral-gray-12"
                  >
                    <PencilSimple size={15} />
                  </button>
                )}
              </div>
              {description ? (
                <p className="mt-1.5 text-[13px] leading-relaxed text-puntt-neutral-gray-11">
                  {description}
                </p>
              ) : null}
            </div>
          )}

          <div data-testid="campaign-project-upcoming-work">
            <h3 className="text-[13px] font-bold text-puntt-neutral-gray-12">
              {t("campaignDocsPanel.nextSteps")}
            </h3>
            <p
              className="mt-0.5 text-[12px] leading-relaxed text-puntt-neutral-gray-10"
              data-testid="campaign-project-upcoming-work-help"
            >
              {t("campaignDocsPanel.nextStepsHelp")}
            </p>
            {upcomingWorkText ? (
              <p className="mt-1.5 text-[13px] leading-relaxed text-puntt-neutral-gray-11">
                {upcomingWorkText}
              </p>
            ) : (
              <p
                className="mt-1.5 text-[13px] leading-relaxed text-puntt-neutral-gray-11"
                data-testid="campaign-project-upcoming-work-empty"
              >
                {t("campaignDocsPanel.noNextSteps")}
              </p>
            )}
          </div>

          {details.length > 0 && (
            <div data-testid="campaign-project-details">
              <h3 className="text-[13px] font-bold text-puntt-neutral-gray-12">
                {t("campaignDocsPanel.projectDetails")}
              </h3>
              <dl className="mt-1.5 flex flex-col gap-2">
                {details.map((entry) => (
                  <div
                    key={entry.key}
                    data-testid={`campaign-project-detail-${entry.key}`}
                  >
                    <dt className="text-[12px] font-semibold text-puntt-neutral-gray-12">
                      {entry.label?.trim() || entry.key}
                    </dt>
                    <dd className="mt-0.5 text-[13px] leading-relaxed text-puntt-neutral-gray-11">
                      {entry.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <UsedConnectorsChips
            connectors={usedConnectors}
            testIdPrefix="project-context"
          />

          {citedClaims.length > 0 ? (
            <CitedClaimsPanel
              citations={citedClaims}
              selectedRuleId={selectedCitationRuleId}
              onSelect={onCitedClaimSelect}
              variant="section"
              testIdPrefix={citedClaimsTestIdPrefix}
            />
          ) : null}

          <div>
            <button
              type="button"
              onClick={() => setDocsOpen((o) => !o)}
              aria-expanded={docsOpen}
              aria-label={t("campaignDocsPanel.toggleReferenceDocuments")}
              data-testid="campaign-docs-toggle"
              className="flex w-full items-center gap-2 rounded-md bg-base-white p-2 text-left"
            >
              <h3 className="flex-1 text-[13px] font-bold text-puntt-neutral-gray-12">
                {t("campaignDocsPanel.referenceDocuments")}
              </h3>
              <CaretDown
                size={14}
                aria-hidden="true"
                className={`shrink-0 text-puntt-neutral-gray-11 transition-transform ${docsOpen ? "" : "-rotate-90"}`}
              />
            </button>

            {docsOpen && (
              <div
                className="mt-2 flex flex-col gap-2"
                data-testid="campaign-docs-body"
              >
                {documents.length === 0 ? (
                  <p
                    className="text-[13px] leading-relaxed text-puntt-neutral-gray-11"
                    data-testid="campaign-docs-empty"
                  >
                    {t("campaignDocsPanel.noDocumentsYet")}
                  </p>
                ) : (
                  documents.map((row, index) => {
                    const isPrimaryFileRow =
                      documents.findIndex(
                        (r) => r.file.fileId === row.file.fileId,
                      ) === index;
                    return (
                      <ReferenceFileRow
                        key={`${row.file.fileId}-v${row.versionOrdinal}`}
                        row={row}
                        showVersionBadge={(row.file.versions?.length ?? 0) > 1}
                        projectId={
                          projectId && isPrimaryFileRow ? projectId : undefined
                        }
                      />
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function referenceRowDisplayName(
  file: AskPunttProjectFile,
  version: AskPunttProjectFile["versions"][number],
): string {
  const trimmed = file.name?.trim();
  if (trimmed && trimmed !== "File") return trimmed;
  return (
    displayFileNameFromSource(version.name) ??
    displayFileNameFromSource(version.source) ??
    displayFileNameFromSource(version.key) ??
    trimmed ??
    "File"
  );
}

function ReferenceFileRow({
  row,
  showVersionBadge,
  projectId,
}: {
  row: ProjectReferenceDocumentRow;
  showVersionBadge: boolean;
  projectId?: string;
}) {
  const { file, version, versionOrdinal } = row;
  const resolvedSource = version.source ?? version.key ?? "";
  const resolvedName = referenceRowDisplayName(file, version);
  const resolvedType = file.type ?? "file";
  const testId = `campaign-file-download-${file.fileId}-v${versionOrdinal}`;

  if (!projectId) {
    return (
      <DownloadableReferenceRow
        source={resolvedSource}
        name={resolvedName}
        type={resolvedType}
        testId={testId}
        versionOrdinal={showVersionBadge ? versionOrdinal : undefined}
      />
    );
  }

  return (
    <FileTagEditor
      testIdPrefix="campaign-file"
      projectId={projectId}
      fileId={file.fileId}
      tags={file.tags}
      chipsBelow
      leading={
        <div className="min-w-0 flex-1">
          <DownloadableReferenceRow
            source={resolvedSource}
            name={resolvedName}
            type={resolvedType}
            testId={testId}
            versionOrdinal={showVersionBadge ? versionOrdinal : undefined}
          />
        </div>
      }
    />
  );
}

function DownloadableReferenceRow({
  source,
  name,
  type,
  testId,
  versionOrdinal,
}: {
  source: string;
  name: string;
  type: string;
  testId: string;
  versionOrdinal?: number;
}) {
  const { t } = useTranslation("ui");
  const [downloading, setDownloading] = useState(false);
  const TypeIcon = fileTypeIcon(type);
  const typeLabel = fileTypeLabel(type, name, t);

  const handleDownload = async () => {
    if (downloading || !source) return;
    setDownloading(true);
    try {
      await downloadAsset(source, name);
    } catch {
      // downloadAsset already handles user-visible failure paths.
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={downloading || !source}
      data-testid={testId}
      aria-label={
        versionOrdinal != null
          ? t("campaignDocsPanel.downloadFileVersion", {
              name,
              version: versionOrdinal,
            })
          : t("campaignDocsPanel.downloadFile", { name })
      }
      className={cx(
        "flex w-full items-center gap-2 px-2 py-2.5 text-left disabled:cursor-not-allowed disabled:opacity-60",
        interactiveSurfaceClasses({
          interactive: true,
          disabled: downloading || !source,
        }),
      )}
    >
      <span
        aria-hidden="true"
        className={interactiveSurfaceIconClasses(
          "flex size-5 shrink-0 items-center justify-center text-puntt-neutral-gray-11",
        )}
      >
        {downloading ? (
          <CircleNotch size={18} className="animate-spin" />
        ) : (
          <TypeIcon size={18} />
        )}
      </span>
      <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-puntt-neutral-gray-12">
        {name}
      </span>
      <span className="flex shrink-0 items-center gap-2">
        {versionOrdinal != null && (
          <span
            className="font-mono rounded border border-puntt-neutral-gray-12 bg-puntt-neutral-gray-12 px-1.5 py-px text-[10px] font-medium text-base-white"
            data-testid={`${testId}-version`}
          >
            v{versionOrdinal}
          </span>
        )}
        <span className="text-[10px] font-medium uppercase tracking-wide text-puntt-neutral-gray-10">
          {typeLabel}
        </span>
      </span>
    </button>
  );
}
